import { computed, ComputedRef, reactive } from 'vue'

const storageKey = 'cookieConsent'
const gaId = 'G-NX91W7ELB7'
const gaCookiePrefix = 'uio' // GA prepends this to its cookie names, matching UiO's other sites
const consentMaxAge = 365 * 24 * 60 * 60 * 1000 // ask again after a year

interface StoredConsent {
  statistics: boolean
  date: number
}

interface State {
  statistics: boolean | undefined // undefined until the user has chosen
  dialogIsOpen: boolean
}

function readStoredConsent(): StoredConsent | undefined {
  try {
    const stored = JSON.parse(localStorage.getItem(storageKey) || 'null') as StoredConsent | null
    if (stored && Date.now() - stored.date < consentMaxAge) return stored
  } catch {
    // storage unavailable or corrupt: treat as no choice made
  }
  return undefined
}

const state: State = reactive({
  statistics: readStoredConsent()?.statistics,
  dialogIsOpen: false,
})

type GtagWindow = { dataLayer?: unknown[]; [key: string]: unknown }
const gtagWindow = window as unknown as GtagWindow

// Google Analytics may only load after the user has accepted statistics cookies
function loadAnalytics(): void {
  const w = gtagWindow
  w[`ga-disable-${gaId}`] = false
  if (document.getElementById('gtag-script')) return
  w.dataLayer = w.dataLayer || []
  // gtag.js only processes Arguments objects, not plain arrays
  function gtag(..._args: unknown[]) {
    // eslint-disable-next-line prefer-rest-params
    w.dataLayer?.push(arguments)
  }
  gtag('js', new Date())
  gtag('config', gaId, { cookie_prefix: gaCookiePrefix })
  const script = document.createElement('script')
  script.id = 'gtag-script'
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`
  document.head.appendChild(script)
}

function stopAnalytics(): void {
  gtagWindow[`ga-disable-${gaId}`] = true
  const domain = location.hostname.split('.').slice(-2).join('.')
  for (const cookie of document.cookie.split(';')) {
    const name = cookie.split('=')[0].trim()
    // Also clears unprefixed _ga cookies set before the prefix was introduced
    if (name.startsWith('_ga') || name.startsWith(gaCookiePrefix + '_')) {
      document.cookie = `${name}=; Max-Age=0; path=/`
      document.cookie = `${name}=; Max-Age=0; path=/; domain=.${domain}`
    }
  }
}

if (state.statistics) loadAnalytics()

interface Actions {
  setStatistics(accepted: boolean): void
  openDialog(): void
  closeDialog(): void
}

interface Getters {
  statistics: ComputedRef<boolean | undefined>
  hasChosen: ComputedRef<boolean>
  dialogIsOpen: ComputedRef<boolean>
}

interface ServiceInterface {
  state: State
  actions: Actions
  getters: Getters
}

function useConsentStore(): ServiceInterface {
  const actions = {
    setStatistics(accepted: boolean): void {
      state.statistics = accepted
      try {
        localStorage.setItem(storageKey, JSON.stringify({ statistics: accepted, date: Date.now() } as StoredConsent))
      } catch {
        // the choice still applies for this page load
      }
      if (accepted) loadAnalytics()
      else stopAnalytics()
    },
    openDialog(): void {
      state.dialogIsOpen = true
    },
    closeDialog(): void {
      state.dialogIsOpen = false
    },
  }

  const getters = {
    get statistics(): ComputedRef<boolean | undefined> {
      return computed(() => state.statistics)
    },
    get hasChosen(): ComputedRef<boolean> {
      return computed(() => state.statistics !== undefined)
    },
    get dialogIsOpen(): ComputedRef<boolean> {
      return computed(() => state.dialogIsOpen)
    },
  }

  return { state, actions, getters }
}

export default useConsentStore
